import {
  Component,
  contentChild,
  effect,
  input,
  InputSignalWithTransform,
  numberAttribute
} from '@angular/core';

import { KeyCode } from '../../domain/key-code.enum';
import { InputDirective } from '../input/input.directive';
import { MaxValueValidator } from '../input/validators/max-validator.directive';
import { MinValueValidator } from '../input/validators/min-validator.directive';
import { NumberService } from '../../services/number/number.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { IconComponent } from '../icon/icon.component';

import type { Signal } from '@angular/core';

enum ValueAction {
  DECREASE = 1,
  INCREASE = -1
}

/**
 * This component creates a number spinner input
 *
 * Description
 *
 * Shows arrows on the right side of input field that could be used to increase or decrease field value
 *
 * @example:
 * <form #nsForm="ngForm">
 *   <td-wb-number-spinner [step]="10">
 *       <input tdWbInput
 *         name="numberSpinner"
 *         min="0"
 *         max="300"
 *         [disabled]="false"
 *         [ngModel]="value">
 *   </td-wb-number-spinner>
 * </form>
 * */
@Component({
  selector: 'td-wb-number-spinner',
  templateUrl: './number-spinner.component.html',
  styleUrls: ['./number-spinner.component.scss'],
  exportAs: 'tdWbNumberSpinner',
  imports: [IconComponent, TranslatePipe],
  host: {
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
    '(focusin)': 'onFocusIn()',
    '(focusout)': 'onFocusOut()',
    '(keydown)': 'onKeyDownHandler($event)',
    '(wheel)': 'onMouseWheelHandler($event)'
  }
})
export class NumberSpinnerComponent {

  step: InputSignalWithTransform<number, unknown> = input<number, unknown>(1, { transform: numberAttribute });

  input: Signal<InputDirective> = contentChild.required(InputDirective);
  minValidator: Signal<MinValueValidator | undefined> = contentChild(MinValueValidator);
  maxValidator: Signal<MaxValueValidator | undefined> = contentChild(MaxValueValidator);

  hover: boolean = false;
  focused: boolean = false;

  constructor() {
    // effect: sets inputClass on the projected InputDirective child — InputDirective is a
    // third-party content child whose class property cannot be bound declaratively from here.
    effect(() => {
      const input = this.input();
      if (input) {
        input.inputClass = 'td-wb-input--square';
      }
    });
  }

  private static getMouseWheelDirection(event: WheelEvent): ValueAction {
    return event.deltaY < 0 ? ValueAction.INCREASE : ValueAction.DECREASE;
  }

  private static getKeyDirection(keyCode: string): ValueAction {
    if (keyCode === KeyCode.ARROW_UP) {
      return ValueAction.INCREASE;
    }
    if (keyCode === KeyCode.ARROW_DOWN) {
      return ValueAction.DECREASE;
    }
  }

  onMouseEnter(): void {
    this.hover = true;
    this.input().setHoverState(true);
  }

  onMouseLeave(): void {
    this.hover = false;
    this.input().setHoverState(false);
  }

  onFocusIn(): void {
    this.focused = true;
    this.input().setFocusState(true);
  }

  onFocusOut(): void {
    this.focused = false;
    this.input().setFocusState(false);
  }

  /**
   * Handles key up/down - increase or decrease input value
   *
   * @param event
   */
  onKeyDownHandler(event: KeyboardEvent): void {
    if (!this.isDisabled()) {
      const direction = NumberSpinnerComponent.getKeyDirection(event.key);
      if (direction !== undefined) {
        event.preventDefault();
        this.updateInputValue(direction);
      }
    }
  }

  /**
   * Handle mouse wheel UP/down - increase or decrease input value
   *
   * @param event
   */
  onMouseWheelHandler(event: Event): void {
    if (!this.isDisabled()) {
      event.preventDefault();
      this.updateInputValue(NumberSpinnerComponent.getMouseWheelDirection(event as WheelEvent));
    }
  }

  /**
   * Button UP click handler
   */
  onIncrement(): void {
    this.updateInputValue(ValueAction.INCREASE);
  }

  /**
   * Button down click handler
   */
  onDecrement(): void {
    this.updateInputValue(ValueAction.DECREASE);
  }

  isDisabled(): boolean {
    return this.input().disabled;
  }

  isError(): boolean {
    return this.input().isErrorState();
  }

  isActive(): boolean {
    return !this.isDisabled() && !this.isError() && (this.hover || this.focused);
  }

  /**
   * Update spinner value
   *
   * @param direction {ValueAction} increase or decrease
   */
  private updateInputValue(direction: ValueAction): void {
    if (!this.isDisabled()) {
      if (!NumberService.isNumber(this.input().getValue())) {
        this.input().setValue(0);
      }
      const nextValue = this.getNextValue(direction);
      if (this.isValidValue(nextValue)) {
        this.input().setValue(nextValue);
      }
    }
  }

  private getNextValue(direction: ValueAction): number {
    const currentValue = this.input().getValue() as number;
    const step = this.step();
    const precision = Math.max(NumberService.getPrecision(currentValue), NumberService.getPrecision(step));
    let nextValue: number;
    if (direction === ValueAction.INCREASE) {
      nextValue = NumberService.round(currentValue + step, precision);
      if (this.minValidator() !== undefined && !this.minValidator().validateValue(nextValue)) {
        nextValue = this.minValidator().min;
      }
    } else {
      nextValue = NumberService.round(currentValue - step, precision);
      if (this.maxValidator() !== undefined && !this.maxValidator().validateValue(nextValue)) {
        nextValue = this.maxValidator().max;
      }
    }
    return nextValue;
  }

  /**
   * Checks if value is in the allowed range
   *
   * @param value {number}
   * @returns
   */
  private isValidValue(value: number): boolean {
    return (this.minValidator() === undefined || this.minValidator().validateValue(value)) &&
      (this.maxValidator() === undefined || this.maxValidator().validateValue(value));
  }

}
